import { Inject, Injectable, OnDestroy } from '@angular/core';
import { debounce, throttle } from 'lodash';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { MonitorActivityType } from './models/monitor-activity-type.enum';
import { MonitorActivity } from './models/monitor-activity.model';
import { MonitorActivityProvider } from './interfaces/monitor-activity-provider.service';
import { MonitorModuleConfig } from '../../models/monitor-module-config.model';
import { fromEvent, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import * as moment from 'moment';
import { DOCUMENT } from '@angular/common';

/**
 * This service is used for collecting UI actions
 * and saving them to the server
 */
@Injectable()
export class MonitorService implements OnDestroy {
  /**
   * The session uniq identifier
   * @private
   */
  private readonly guid: any;

  /**
   * The list of accumulated activities
   * @private
   */
  private activities: MonitorActivity[] = [];

  /**
   * The previous route state
   * @private
   */
  private previousRoute: NavigationEnd;

  /**
   * The previous route snapshot
   * @private
   */
  private previousRouteSnapshot: ActivatedRouteSnapshot;

  /**
   * The instance of service for saving logs
   * @private
   */
  private monitorActivityProvider: MonitorActivityProvider;

  /**
   * The router events subscription
   * @private
   */
  private eventsSubscriptions = new Subscription();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private monitorModuleConfig: MonitorModuleConfig,
    @Inject(DOCUMENT) private document
  ) {
    this.guid = uuidv4();
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    this.eventsSubscriptions.unsubscribe();
  }

  /**
   * This method is used for initializing monitor service
   * @param monitorActivityProvider The service for saving logs
   */
  initialize(monitorActivityProvider: MonitorActivityProvider) {
    if (!monitorActivityProvider) {
      throw new Error('Please provide monitorActivityProvider');
    }
    this.monitorActivityProvider = monitorActivityProvider;

    const routerEventsSubscription = this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) {
        const previousState = this.getRouteState(this.previousRouteSnapshot);
        const currentState = this.getRouteState(this.activatedRoute.snapshot);
        this.previousRouteSnapshot = this.activatedRoute.snapshot;

        const from = { ...this.previousRoute };
        const to = <NavigationEnd>event;
        this.previousRoute = <NavigationEnd>event;

        this.handleActivity({
          url_current: this.prefixWithDomain(from.url),
          url_redirect: this.prefixWithDomain(to.url),
          route_current: previousState,
          route_redirect: currentState,
          full_route_current: this.prefixWithDomain(previousState),
          full_route_redirect: this.prefixWithDomain(currentState),
          action_type: MonitorActivityType.Redirect,
        });
      }
    });

    const documentClickSubscription = fromEvent(this.document, 'click').subscribe((event: MouseEvent) => {
      const elem = <Element>event.target;
      const path = event
        .composedPath()
        .map((x: any) => {
          let name = '';
          if (x.tagName) {
            name += x.tagName.toLowerCase();
          }
          if (!!x.className && typeof x.className === 'string') {
            name += `.${x.className.replace(new RegExp(' ', 'g'), '.')}`;
          }
          if (!!name) {
            return name;
          }
        })
        .filter((item: string) => item)
        .join(' > ');
      let text = '';

      if (elem.textContent && elem.textContent !== ' ') {
        text = elem.textContent.trim().substring(0, 50);
      } else {
        const firstSpan = elem.querySelector('span');
        if (firstSpan) {
          text = firstSpan.innerText.trim().substring(0, 50);
        }
      }

      const currentState = this.getRouteState(this.activatedRoute.snapshot);
      this.handleActivity({
        point_coordinate_x: event.clientX,
        point_coordinate_y: event.clientY,
        action_type: MonitorActivityType.Click,
        url_current: this.prefixWithDomain(this.activatedRoute.snapshot['_routerState'].url),
        route_current: currentState,
        full_route_current: this.prefixWithDomain(currentState),
        action_subject: path,
        action_info: text,
      });
    });

    this.eventsSubscriptions.add(routerEventsSubscription);
    this.eventsSubscriptions.add(documentClickSubscription);

    this.saveDebounce();
  }

  /**
   * This method is used for handling unload event
   */
  onBeforeUnload() {
    this.handleActivity(
      {
        action_type: MonitorActivityType.Redirect,
        url_current: this.activatedRoute.snapshot['_routerState'].url,
      },
      true
    );
  }

  /**
   * The helper method for throttling click
   */
  clickThrottle = throttle((activity) => {
    this.addActivity(activity);
  }, this.monitorModuleConfig.serviceConfig.THROTTLE_CLICK_TIMEOUT);

  /**
   * The helper method for idling
   */
  idleDebounce = debounce(() => {
    this.saveActivities();
  }, this.monitorModuleConfig.serviceConfig.IDLE_TIMEOUT);

  /**
   * The helper method for saving with debounce
   */
  saveDebounce = debounce(() => {
    this.saveActivities();
  }, this.monitorModuleConfig.serviceConfig.SAVE_TIMEOUT);

  /**
   * This method is used for handling activity
   * @param initial The initial activity properties
   * @param save The flag indicating that we should save activity right now
   * @private
   */
  private handleActivity(initial: Partial<MonitorActivity>, save = false) {
    this.monitorActivityProvider.isAuthenticated().then((isAuthenticated: boolean) => {
      const activity: MonitorActivity = {
        ...{
          time: moment().utc().toISOString(),
          session_id: this.guid,
          isAuth: isAuthenticated,
          user_browser_resolution: window.innerWidth + 'x' + window.innerHeight,
          point_coordinate_x: 0,
          point_coordinate_y: 0,
          route_current: '',
          full_route_current: '',
          url_current: '',
          route_redirect: '',
          full_route_redirect: '',
          url_redirect: '',
          action_subject: '',
          action_type: null,
          action_info: '',
        },
        ...initial,
      };
      if (this.activities.length > 1) {
        if (
          this.activities[this.activities.length - 2].action_subject === activity.action_subject &&
          this.activities[this.activities.length - 1].action_subject === activity.action_subject
        ) {
          this.clickThrottle(activity);
          return;
        }
      }
      this.addActivity(activity, save);
    });
  }

  /**
   * This method is used to accumulate activity
   * @param activity The activity
   * @param save The flag indicating that we should save activity right now
   * @private
   */
  private addActivity(activity: MonitorActivity, save = false) {
    this.activities.push(activity);
    if (this.activities.length > this.monitorModuleConfig.serviceConfig.SAVE_COUNT || save) {
      this.idleDebounce.cancel();
      this.saveDebounce.cancel();
      this.saveActivities();
    } else {
      this.idleDebounce();
    }
  }

  /**
   * This method is used for saving pending activities from the buffer
   * @private
   */
  private saveActivities() {
    if (this.activities.length > 0) {
      this.monitorActivityProvider
        .save(this.activities)
        .toPromise()
        .then(() => (this.activities = []));
    }
    this.saveDebounce();
  }

  /**
   * This method is used for getting route state
   * @param route The activated route
   * @private
   */
  private getRouteState(route: ActivatedRouteSnapshot): string {
    const paths: string[] = [];
    while (route) {
      if (route.routeConfig?.path) {
        paths.push(route.routeConfig.path);
      }
      route = route.firstChild;
    }
    return paths.join('/');
  }

  /**
   * This method is used for getting prefixed path with domain name
   * @param path The path to add prefix
   * @private
   */
  private prefixWithDomain(path: string): string {
    if (path) {
      const origin = window.location.origin;
      return origin + (path.startsWith('/') ? '' : '/') + path;
    }
    return '';
  }
}
